<!-- FieldInventoryRequestPanel.vue - Panel para solicitar ítems de inventario -->
<template>
  <div class="field-inventory-request-panel">
    <!-- Resumen de presupuesto -->
    <v-card class="mb-4" variant="outlined" color="primary">
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="3">
            <div class="text-center">
              <div class="text-h5 font-weight-bold">RD$ {{ formatNumber(budgetSummary.total_budget) }}</div>
              <div class="text-caption">Presupuesto Total</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-center">
              <div class="text-h5 font-weight-bold text-error">RD$ {{ formatNumber(budgetSummary.total_spent) }}</div>
              <div class="text-caption">Gastos Registrados</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-center">
              <div class="text-h5 font-weight-bold text-warning">RD$ {{ formatNumber(budgetSummary.total_committed || 0) }}</div>
              <div class="text-caption">Comprometido</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-center">
              <div class="text-h5 font-weight-bold text-success">RD$ {{ formatNumber(budgetSummary.total_available) }}</div>
              <div class="text-caption">Disponible</div>
            </div>
          </v-col>
        </v-row>
        <v-progress-linear
          :model-value="budgetPercentUsed"
          :color="budgetPercentUsed > 80 ? 'error' : budgetPercentUsed > 60 ? 'warning' : 'success'"
          height="10"
          class="mt-3"
          rounded
        >
          <template v-slot:default>
            <strong>{{ budgetPercentUsed.toFixed(1) }}% usado</strong>
          </template>
        </v-progress-linear>
        <!-- Detalle de comprometido si existe -->
        <v-alert
          v-if="budgetSummary.total_committed > 0"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-2"
        >
          <v-icon start size="small">mdi-clock-outline</v-icon>
          RD$ {{ formatNumber(budgetSummary.total_committed) }} en solicitudes pendientes de entrega
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Tabs: Nueva Solicitud / Historial -->
    <v-tabs v-model="activeTab" bg-color="primary" class="mb-4">
      <v-tab value="new">
        <v-icon start>mdi-plus-circle</v-icon>
        Nueva Solicitud
      </v-tab>
      <v-tab value="history">
        <v-icon start>mdi-history</v-icon>
        Mis Solicitudes
      </v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <!-- Tab: Nueva Solicitud -->
      <v-window-item value="new">
        <v-card variant="outlined">
          <v-card-title>
            <v-icon start>mdi-package-variant-plus</v-icon>
            Solicitar Ítems para Agencia
          </v-card-title>
          
          <v-card-text>
            <v-form ref="requestForm" v-model="formValid">
              <!-- Selección de Agencia -->
              <v-select
                v-model="newRequest.agency_id"
                :items="agencies"
                item-title="name"
                item-value="id"
                label="Agencia Destino"
                prepend-icon="mdi-store"
                :rules="[v => !!v || 'Seleccione una agencia']"
                density="compact"
                class="mb-3"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template v-slot:subtitle>
                      <span class="text-caption">{{ item.raw.group?.name || 'Sin grupo' }}</span>
                    </template>
                  </v-list-item>
                </template>
              </v-select>

              <!-- Búsqueda de Producto -->
              <v-autocomplete
                v-model="newRequest.inventory_id"
                :items="catalogItems"
                :loading="loadingCatalog"
                :search-input.sync="catalogSearch"
                item-title="name"
                item-value="id"
                label="Producto"
                prepend-icon="mdi-magnify"
                :rules="[v => !!v || 'Seleccione un producto']"
                density="compact"
                class="mb-3"
                @update:search="debouncedSearchCatalog"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template v-slot:prepend>
                      <v-chip size="x-small" color="primary">{{ item.raw.code }}</v-chip>
                    </template>
                    <template v-slot:subtitle>
                      <span>Stock: {{ item.raw.available_stock }} | RD$ {{ formatNumber(item.raw.unit_price) }}/{{ item.raw.unit }}</span>
                    </template>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <!-- Info del producto seleccionado -->
              <v-alert
                v-if="selectedProduct"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                <div class="d-flex justify-space-between">
                  <span><strong>{{ selectedProduct.name }}</strong> ({{ selectedProduct.code }})</span>
                  <span>RD$ {{ formatNumber(selectedProduct.unit_price) }} / {{ selectedProduct.unit }}</span>
                </div>
                <div class="text-caption">Stock disponible: {{ selectedProduct.available_stock }}</div>
              </v-alert>

              <!-- Cantidad -->
              <v-text-field
                v-model.number="newRequest.quantity"
                type="number"
                label="Cantidad"
                prepend-icon="mdi-numeric"
                :rules="[
                  v => !!v || 'Ingrese la cantidad',
                  v => v > 0 || 'Debe ser mayor a 0',
                  v => !selectedProduct || v <= selectedProduct.available_stock || `Máximo disponible: ${selectedProduct.available_stock}`
                ]"
                min="1"
                :max="selectedProduct?.available_stock || 999999"
                density="compact"
                class="mb-3"
              ></v-text-field>

              <!-- Costo estimado -->
              <v-alert
                v-if="estimatedCost > 0"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                <div class="d-flex justify-space-between align-center">
                  <span>Costo Estimado:</span>
                  <strong class="text-h6">RD$ {{ formatNumber(estimatedCost) }}</strong>
                </div>
                <div class="text-caption mt-1">
                  <v-icon size="small" color="success" class="mr-1">mdi-domain</v-icon>
                  <span class="text-success font-weight-medium">Cubierto por la empresa</span>
                  <span class="text-medium-emphasis"> - No afecta su fondo disponible</span>
                </div>
              </v-alert>

              <!-- Prioridad -->
              <v-select
                v-model="newRequest.priority"
                :items="priorityOptions"
                item-title="text"
                item-value="value"
                label="Prioridad"
                prepend-icon="mdi-flag"
                density="compact"
                class="mb-3"
              ></v-select>

              <!-- Justificación -->
              <v-textarea
                v-model="newRequest.justification"
                label="Justificación"
                prepend-icon="mdi-text"
                rows="2"
                density="compact"
                class="mb-3"
                placeholder="Explique brevemente por qué necesita estos ítems..."
              ></v-textarea>

              <!-- Fecha esperada -->
              <v-text-field
                v-model="newRequest.expected_delivery_date"
                type="date"
                label="Fecha esperada de entrega"
                prepend-icon="mdi-calendar"
                density="compact"
                :min="minDeliveryDate"
              ></v-text-field>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey"
              variant="text"
              @click="resetForm"
            >
              Cancelar
            </v-btn>
            <v-btn
              color="primary"
              :loading="submitting"
              :disabled="!formValid"
              @click="submitRequest"
            >
              <v-icon start>mdi-send</v-icon>
              Enviar Solicitud
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-window-item>

      <!-- Tab: Historial de Solicitudes -->
      <v-window-item value="history">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon start>mdi-format-list-bulleted</v-icon>
            Historial de Solicitudes
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-refresh"
              variant="text"
              size="small"
              @click="loadRequests"
              :loading="loadingRequests"
            ></v-btn>
          </v-card-title>

          <v-card-text>
            <!-- Filtro de estado -->
            <v-chip-group v-model="statusFilter" class="mb-3">
              <v-chip filter value="">Todas</v-chip>
              <v-chip filter value="pending" color="warning">Pendientes</v-chip>
              <v-chip filter value="approved" color="info">Aprobadas</v-chip>
              <v-chip filter value="delivered" color="success">Entregadas</v-chip>
              <v-chip filter value="rejected" color="error">Rechazadas</v-chip>
            </v-chip-group>

            <!-- Lista de solicitudes -->
            <v-list v-if="filteredRequests.length" lines="two">
              <v-list-item
                v-for="request in filteredRequests"
                :key="request.id"
                class="mb-2 rounded border"
                @click="showRequestDetail(request)"
                style="cursor: pointer"
              >
                <template v-slot:prepend>
                  <v-avatar :color="getStatusColor(request.status)" size="40">
                    <v-icon color="white">{{ getStatusIcon(request.status) }}</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title>
                  <strong>{{ request.product?.name || 'Producto' }}</strong>
                  <v-chip size="x-small" class="ml-2" :color="getStatusColor(request.status)">
                    {{ getStatusText(request.status) }}
                  </v-chip>
                </v-list-item-title>

                <v-list-item-subtitle>
                  <div>
                    <v-icon size="14">mdi-store</v-icon>
                    {{ request.agency?.name || 'N/A' }} |
                    <v-icon size="14">mdi-package</v-icon>
                    {{ request.requested_quantity }} unidades |
                    <v-icon size="14">mdi-currency-usd</v-icon>
                    RD$ {{ formatNumber(request.estimated_cost || 0) }}
                  </div>
                  <div class="text-caption">
                    {{ formatDate(request.requested_date) }}
                    <span v-if="request.approved_by" class="ml-2">
                      · Aprobada por: {{ request.approved_by.name }}
                    </span>
                  </div>
                </v-list-item-subtitle>

                <template v-slot:append>
                  <div class="d-flex align-center">
                    <v-chip
                      size="small"
                      :color="getPriorityColor(request.priority)"
                      variant="outlined"
                      class="mr-2"
                      >
                      {{ request.priority }}
                    </v-chip>
                    <v-icon size="small" color="grey">mdi-chevron-right</v-icon>
                  </div>
                </template>
              </v-list-item>
            </v-list>

            <v-alert v-else type="info" variant="tonal">
              No hay solicitudes {{ statusFilter ? `con estado "${getStatusText(statusFilter)}"` : '' }}
            </v-alert>

            <!-- Paginación -->
            <v-pagination
              v-if="requestsPagination.total_pages > 1"
              v-model="currentPage"
              :length="requestsPagination.total_pages"
              :total-visible="5"
              density="compact"
              class="mt-3"
              @update:model-value="loadRequests"
            ></v-pagination>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- Diálogo de Detalle de Solicitud con Timeline -->
    <v-dialog v-model="detailDialog.show" max-width="600">
      <v-card v-if="detailDialog.request">
        <v-card-title class="d-flex align-center">
          <v-icon start :color="getStatusColor(detailDialog.request.status)">
            {{ getStatusIcon(detailDialog.request.status) }}
          </v-icon>
          Solicitud #{{ detailDialog.request.id }}
          <v-chip 
            size="small" 
            :color="getStatusColor(detailDialog.request.status)" 
            class="ml-2"
          >
            {{ getStatusText(detailDialog.request.status) }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="detailDialog.show = false"></v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <!-- Info del Producto -->
          <v-card variant="outlined" class="mb-4">
            <v-card-text class="pa-3">
              <div class="d-flex align-center mb-2">
                <v-icon start color="primary">mdi-package-variant</v-icon>
                <strong class="text-h6">{{ detailDialog.request.product?.name }}</strong>
              </div>
              <v-chip size="small" color="primary" variant="outlined" class="mr-2">
                {{ detailDialog.request.product?.code }}
              </v-chip>
              <span class="text-caption">
                RD$ {{ formatNumber(detailDialog.request.product?.unit_price || 0) }} / {{ detailDialog.request.product?.unit }}
              </span>
            </v-card-text>
          </v-card>

          <!-- Detalles de Cantidades -->
          <v-row class="mb-4">
            <v-col cols="4" class="text-center">
              <div class="text-h5">{{ detailDialog.request.requested_quantity }}</div>
              <div class="text-caption">Solicitadas</div>
            </v-col>
            <v-col cols="4" class="text-center">
              <div class="text-h5 text-info">{{ detailDialog.request.approved_quantity || '-' }}</div>
              <div class="text-caption">Aprobadas</div>
            </v-col>
            <v-col cols="4" class="text-center">
              <div class="text-h5 text-success">{{ detailDialog.request.delivered_quantity || '-' }}</div>
              <div class="text-caption">Entregadas</div>
            </v-col>
          </v-row>

          <!-- Destino -->
          <div class="mb-4">
            <v-icon start size="small">mdi-store</v-icon>
            <strong>Destino:</strong> {{ detailDialog.request.agency?.name || 'N/A' }}
            <span v-if="detailDialog.request.group" class="text-caption ml-2">
              ({{ detailDialog.request.group.name }})
            </span>
          </div>

          <!-- Costo -->
          <v-alert 
            :type="detailDialog.request.status === 'delivered' ? 'success' : 'info'"
            variant="tonal" 
            density="compact" 
            class="mb-4"
          >
            <div class="d-flex justify-space-between align-center">
              <span>{{ detailDialog.request.status === 'delivered' ? 'Costo Final:' : 'Costo Estimado:' }}</span>
              <strong class="text-h6">RD$ {{ formatNumber(detailDialog.request.estimated_cost || 0) }}</strong>
            </div>
          </v-alert>

          <!-- Justificación -->
          <div v-if="detailDialog.request.justification" class="mb-4">
            <div class="text-caption text-grey">Justificación:</div>
            <div class="text-body-2">{{ detailDialog.request.justification }}</div>
          </div>

          <!-- Razón de rechazo -->
          <v-alert 
            v-if="detailDialog.request.rejection_reason" 
            type="error" 
            variant="tonal" 
            density="compact" 
            class="mb-4"
          >
            <strong>Razón del rechazo:</strong><br>
            {{ detailDialog.request.rejection_reason }}
          </v-alert>

          <!-- Notas de entrega -->
          <v-alert 
            v-if="detailDialog.request.delivery_notes" 
            type="success" 
            variant="tonal" 
            density="compact" 
            class="mb-4"
          >
            <strong>Notas de entrega:</strong><br>
            {{ detailDialog.request.delivery_notes }}
          </v-alert>

          <!-- Timeline -->
          <div class="text-subtitle-2 mb-2">
            <v-icon start size="small">mdi-timeline-clock</v-icon>
            Historial de la Solicitud
          </div>
          
          <v-timeline density="compact" side="end">
            <v-timeline-item
              v-for="(event, index) in detailDialog.request.timeline || []"
              :key="index"
              :dot-color="getTimelineColor(event.event)"
              size="small"
            >
              <template v-slot:icon>
                <v-icon size="x-small" color="white">{{ getTimelineIcon(event.event) }}</v-icon>
              </template>
              <div class="d-flex flex-column">
                <strong>{{ event.label }}</strong>
                <span class="text-caption">{{ formatDateTime(event.date) }}</span>
                <span v-if="event.by" class="text-caption text-grey">Por: {{ event.by }}</span>
              </div>
            </v-timeline-item>
            
            <!-- Estado pendiente si no hay timeline completo -->
            <v-timeline-item
              v-if="!detailDialog.request.timeline?.length"
              dot-color="warning"
              size="small"
            >
              <div class="d-flex flex-column">
                <strong>Solicitud creada</strong>
                <span class="text-caption">{{ formatDateTime(detailDialog.request.requested_date) }}</span>
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            variant="text" 
            @click="detailDialog.show = false"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import ApiClient from '@/services/api/api-client'
// P13-V2A CLOSURE REPAIR (FINDING A): la creación comercial de esta entrada VIVA se ejecuta a
// través de la autoridad de ciclo de vida de Idempotency-Key del frontend (una sola
// implementación, compartida con el resto del inventario). Las LECTURAS siguen usando ApiClient.
import { inventoryService } from '@/services/inventoryService'
import websocketService from '@/services/websocket.service'

// Props
const props = defineProps({
  agencies: {
    type: Array,
    default: () => []
  }
})

// Emits
const emit = defineEmits(['request-created'])

// State
const activeTab = ref('new')
const formValid = ref(false)
const submitting = ref(false)
const loadingCatalog = ref(false)
const loadingRequests = ref(false)
const catalogSearch = ref('')
const catalogItems = ref([])
const requests = ref([])
const statusFilter = ref('')
const currentPage = ref(1)
const requestsPagination = ref({ total_pages: 1, total_items: 0 })

const budgetSummary = ref({
  total_budget: 0,
  total_spent: 0,
  total_available: 0,
  groups: []
})

const newRequest = ref({
  agency_id: null,
  inventory_id: null,
  quantity: 1,
  priority: 'medium',
  justification: '',
  expected_delivery_date: null
})

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

const detailDialog = ref({
  show: false,
  request: null
})

const priorityOptions = [
  { value: 'low', text: 'Baja' },
  { value: 'medium', text: 'Media' },
  { value: 'high', text: 'Alta' },
  { value: 'critical', text: 'Crítica' }
]

// Computed
const selectedProduct = computed(() => {
  if (!newRequest.value.inventory_id) return null
  return catalogItems.value.find(i => i.id === newRequest.value.inventory_id)
})

const estimatedCost = computed(() => {
  if (!selectedProduct.value || !newRequest.value.quantity) return 0
  return selectedProduct.value.unit_price * newRequest.value.quantity
})

const budgetPercentUsed = computed(() => {
  if (!budgetSummary.value.total_budget) return 0
  // Incluir gastado + comprometido en el porcentaje
  const used = (budgetSummary.value.total_spent || 0) + (budgetSummary.value.total_committed || 0)
  return (used / budgetSummary.value.total_budget) * 100
})

const minDeliveryDate = computed(() => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow.toISOString().split('T')[0]
})

const filteredRequests = computed(() => {
  if (!statusFilter.value) return requests.value
  return requests.value.filter(r => r.status === statusFilter.value)
})

// Methods
const formatNumber = (num) => {
  return new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)
}

const formatDate = (date) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getStatusColor = (status) => {
  const colors = {
    pending: 'warning',
    approved: 'info',
    in_transit: 'purple',
    delivered: 'success',
    rejected: 'error',
    cancelled: 'grey'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status) => {
  const icons = {
    pending: 'mdi-clock-outline',
    approved: 'mdi-check',
    in_transit: 'mdi-truck',
    delivered: 'mdi-check-all',
    rejected: 'mdi-close',
    cancelled: 'mdi-cancel'
  }
  return icons[status] || 'mdi-help'
}

const getStatusText = (status) => {
  const texts = {
    pending: 'Pendiente',
    approved: 'Aprobada',
    in_transit: 'En Tránsito',
    delivered: 'Entregada',
    rejected: 'Rechazada',
    cancelled: 'Cancelada'
  }
  return texts[status] || status
}

const getPriorityColor = (priority) => {
  const colors = {
    low: 'grey',
    medium: 'info',
    high: 'warning',
    critical: 'error'
  }
  return colors[priority] || 'grey'
}

const getTimelineColor = (event) => {
  const colors = {
    created: 'primary',
    approved: 'info',
    rejected: 'error',
    delivered: 'success',
    cancelled: 'grey'
  }
  return colors[event] || 'grey'
}

const getTimelineIcon = (event) => {
  const icons = {
    created: 'mdi-plus',
    approved: 'mdi-check',
    rejected: 'mdi-close',
    delivered: 'mdi-truck-check',
    cancelled: 'mdi-cancel'
  }
  return icons[event] || 'mdi-circle'
}

const formatDateTime = (date) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const showRequestDetail = (request) => {
  detailDialog.value.request = request
  detailDialog.value.show = true
}

let searchTimeout = null
const debouncedSearchCatalog = (search) => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    loadCatalog(search)
  }, 300)
}

const loadCatalog = async (search = '') => {
  loadingCatalog.value = true
  try {
    const response = await ApiClient.get('/field-supervisor/inventory/catalog', {
      params: { search, limit: 50 }
    })
    // ApiClient.get() ya extrae response.data
    if (response?.success && response?.data) {
      catalogItems.value = response.data.items || []
    }
  } catch (error) {
    // P13-V2B (Property 3): un fallo de carga es VISIBLE — no un catálogo vacío "exitoso".
    snackbar.value = { show: true, message: `❌ No se pudo cargar el catálogo: ${error?.response?.data?.error || error?.message || 'error'}`, color: 'error' }
  } finally {
    loadingCatalog.value = false
  }
}

const loadBudgetSummary = async () => {
  try {
    console.log('💰 Cargando resumen de presupuesto...')
    const response = await ApiClient.get('/field-supervisor/budget/summary')
    console.log('💰 Respuesta de presupuesto:', response)
    // ApiClient.get() ya extrae response.data, entonces:
    // response = { success: true, data: { total_budget, ... } }
    if (response?.success && response?.data) {
      budgetSummary.value = response.data
    }
  } catch (error) {
    // P13-V2B (Property 3): el presupuesto no disponible se señala, no se omite en silencio.
    snackbar.value = { show: true, message: `⚠️ Presupuesto no disponible: ${error?.response?.data?.error || error?.message || 'error'}`, color: 'warning' }
  }
}

const loadRequests = async () => {
  loadingRequests.value = true
  try {
    const response = await ApiClient.get('/field-supervisor/inventory/requests', {
      params: {
        page: currentPage.value,
        limit: 10,
        status: statusFilter.value || undefined
      }
    })
    // ApiClient.get() ya extrae response.data
    if (response?.success && response?.data) {
      requests.value = response.data.requests || []
      requestsPagination.value = response.data.pagination || { total_pages: 1 }
    }
  } catch (error) {
    // P13-V2B (Property 3): un fallo de carga es VISIBLE — no "sin solicitudes" falso.
    snackbar.value = { show: true, message: `❌ No se pudieron cargar tus solicitudes: ${error?.response?.data?.error || error?.message || 'error'}`, color: 'error' }
  } finally {
    loadingRequests.value = false
  }
}

const submitRequest = async () => {
  submitting.value = true
  try {
    // Sin clave de idempotencia esta ruta responde 400 IDEMPOTENCY_KEY_REQUIRED tras V2A; el
    // servicio la deriva del intento (reintento del mismo intento = misma clave = replay).
    const response = await inventoryService.createFieldSupervisorStockRequest(newRequest.value)
    if (response.data?.success) {
      snackbar.value = {
        show: true,
        message: '✅ Solicitud creada exitosamente',
        color: 'success'
      }
      resetForm()
      loadRequests()
      loadBudgetSummary()
      emit('request-created', response.data.data)
    }
  } catch (error) {
    snackbar.value = {
      show: true,
      message: `❌ ${error.response?.data?.error || 'Error al crear solicitud'}`,
      color: 'error'
    }
  } finally {
    submitting.value = false
  }
}

const resetForm = () => {
  newRequest.value = {
    agency_id: null,
    inventory_id: null,
    quantity: 1,
    priority: 'medium',
    justification: '',
    expected_delivery_date: null
  }
}

// WebSocket handlers para actualizaciones en tiempo real
const handleRequestCreated = (data) => {
  console.log('🔔 WebSocket: Nueva solicitud creada', data)
  // Recargar lista de solicitudes
  loadRequests()
}

const handleRequestUpdated = (data) => {
  console.log('🔔 WebSocket: Solicitud actualizada', data)
  
  // Actualizar la lista de solicitudes
  loadRequests()
  // Actualizar el presupuesto (puede haber cambiado si se entregó)
  loadBudgetSummary()
  
  // Si el diálogo de detalle está abierto y es la misma solicitud, actualizarlo
  if (detailDialog.value.show && detailDialog.value.request) {
    const updatedId = data?.id || data?.request?.id
    if (updatedId && detailDialog.value.request.id === updatedId) {
      // Actualizar el objeto en el diálogo con los nuevos datos
      if (data.request) {
        detailDialog.value.request = { ...detailDialog.value.request, ...data.request }
      } else if (data.status) {
        detailDialog.value.request.status = data.status
        if (data.approved_quantity) detailDialog.value.request.approved_quantity = data.approved_quantity
        if (data.delivered_at) detailDialog.value.request.delivered_at = data.delivered_at
        if (data.rejection_reason) detailDialog.value.request.rejection_reason = data.rejection_reason
      }
      
      // Mostrar notificación visual del cambio
      const statusMessages = {
        approved: '✅ Tu solicitud ha sido aprobada',
        rejected: '❌ Tu solicitud ha sido rechazada',
        delivered: '📦 Tu solicitud ha sido entregada',
        cancelled: '⚪ La solicitud ha sido cancelada'
      }
      const newStatus = data.status || data.request?.status
      if (newStatus && statusMessages[newStatus]) {
        snackbar.value = {
          show: true,
          message: statusMessages[newStatus],
          color: newStatus === 'approved' ? 'success' : 
                 newStatus === 'delivered' ? 'info' : 
                 newStatus === 'rejected' ? 'error' : 'warning'
        }
      }
    }
  }
}

// Watchers
watch(statusFilter, () => {
  currentPage.value = 1
  loadRequests()
})

// Lifecycle
onMounted(() => {
  loadCatalog()
  loadBudgetSummary()
  loadRequests()
  
  // Suscribirse a eventos WebSocket de solicitudes de inventario
  console.log('🔌 WebSocket: Listeners de solicitudes de inventario registrados')
})

onUnmounted(() => {
  // Limpiar listeners WebSocket al desmontar el componente
  console.log('🔌 WebSocket: Listeners de solicitudes de inventario removidos')
})
</script>

<style scoped>
.field-inventory-request-panel {
  height: 100%;
}
</style>
